import {
  Entry,
  EntryVersion,
  LimitPaginationParams,
  PagePaginationParams,
} from '@/components/cms/types/entry';

import { apiCallStrapi } from '@/utils/fetch';
import { Logger } from './Logging';

const logger = new Logger('src/lib/cms.ts');

export type SinglePage<T extends Entry = Entry> = {
  data: {
    id: string;
    attributes: T;
  };
};

export type Page<T extends Entry = Entry> = {
  data: SinglePage<T>['data'][];
  meta?: {
    pagination: {
      page: number;
      pageCount: number;
      pageSize: number;
      total: number;
    };
  };
  error?: {
    status: number;
    name: string;
    message: string;
    details: object;
  };
};

/*
 * apiPath: is the endpoint to fetch data from the CMS
 * urlPrefix: is the prefix to add to the slug of the page when creating static content.
 * So if if we would have "hub/collection-type" called "company" and the urlPrefix "company" the url would be "/company/about"
 * urlPathStartsWith: is used when we have hub "blog" but by default blog slug doesn't contain "blog" in slug
 */
export const paths = [
  {
    apiPath: '/pages',
    urlPrefix: '',
    urlPathStartsWith: null,
  },
  {
    apiPath: '/blog-topics',
    urlPrefix: '',
    urlPathStartsWith: 'blog/topics',
  },
  {
    apiPath: '/blogs',
    urlPrefix: '',
    urlPathStartsWith: 'blog',
  },
] as const;

export type APIPath = (typeof paths)[number]['apiPath'];

export async function getLinks({ apiPath }: { apiPath?: APIPath } = {}) {
  const currentPath = paths.find((path) => path.apiPath === apiPath);
  const selectedPaths = currentPath ? [currentPath] : paths;

  const requestsPromise = selectedPaths.map(async ({ apiPath, urlPrefix }) => {
    const query = {
      pagination: {
        page: 1,
        pageSize: 1000,
      },
    };

    const response = await apiCallStrapi<Page>({
      path: apiPath,
      query,
      method: 'GET',
    });
    const data = response?.data;

    data?.map((page) => {
      const slug = page.attributes.slug;
      page.attributes.slug =
        slug === '/' ? '/' : `${urlPrefix ? urlPrefix + '/' : ''}${slug}`;
      page.attributes.originalSlug = slug;
      page.attributes.prefixSlug = urlPrefix;
      page.attributes.apiPath = apiPath;
    });

    return data ?? null;
  });

  const requests = await Promise.allSettled(requestsPromise);
  const links = requests?.map((request) => {
    if (request.status === 'rejected') {
      logger.error(request.reason);
      return [];
    }

    return request.value;
  });

  return links.flat() ?? null;
}

export async function getStory({
  slug,
  apiPath,
  status,
}: {
  slug?: string;
  apiPath: string;
  status?: EntryVersion;
}) {
  const query = {
    pLevel: 'deep',
    ...(slug
      ? {
          filters: {
            slug: {
              $eq: slug,
            },
          },
        }
      : {}),
  };

  const response = await apiCallStrapi<Page>({
    path: apiPath,
    query,
    method: 'GET',
    status,
  });

  const data = response?.data;
  const meta = response?.meta;

  return {
    data: Array.isArray(data) ? data[0] : data,
    meta: meta ?? null,
  };
}

export async function getEntries<T extends Entry = Entry>({
  contentType,
  query = {},
  signal,
}: {
  contentType: string;
  query?: {
    pLevel?: string;
    populate?: string | Record<string, any>;
    pagination?: LimitPaginationParams | PagePaginationParams;
    filters?: Record<string, any>;
    sort?: string | string[];
  };
  signal?: AbortSignal | null;
}) {
  if (!query.pagination) {
    query.pagination = {
      page: 1,
      pageSize: 10,
    };
  }

  const response = await apiCallStrapi<Page<T>>({
    path: contentType,
    query,
    signal,
  });

  return response;
}

export function flattenEntry<T extends { id: string; attributes: Entry }>(
  entry: T
): { id: string } & T['attributes'] {
  return {
    id: entry.id,
    ...entry.attributes,
  };
}
